/* eslint-disable i18next/no-literal-string */
import * as Sentry from '@sentry/react';

import React, { useEffect } from 'react';
import { ClinicianResponse, ClinicResponse } from './api/generated/models';
import { config } from './config';
import { hasStaleUserSession, identifyUser, resetUser, sanitizeSegmentation, trackEvent } from './countly/countly';

const replayIntegration = Sentry.replayIntegration();

/**
 * Triggers a Sentry Replay recording session while the component is mounted.
 */
export const useSentryReplay = () => {
  React.useEffect(() => {
    replayIntegration.start();
    return () => {
      replayIntegration.stop();
    };
  }, []);
};

export const initAnalytics = () => {
  try {
    Sentry.init({
      dsn: config.SENTRY_DSN,
      tunnel: config.SENTRY_TUNNEL,
      environment: config.ENV,
      release: config.COMMIT_SHA,

      integrations: [
        Sentry.browserTracingIntegration(),
        replayIntegration,
      ],

      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.1,
    });
  } catch (error) {
    console.error('Sentry initialization error:', error);
  }
};

export const useAnalyticsUserIdentity = (user: ClinicianResponse & { clinics: ClinicResponse[] } | null) => {
  const prevUserIdRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      Sentry.setTag('clinician_id', null);
      Sentry.setTag('clinic_ids', null);
      if (prevUserIdRef.current !== null || hasStaleUserSession()) {
        resetUser();
        prevUserIdRef.current = null;
      }
      return;
    }
    const userIdentity = {
      id: '' + user.id,
      username: `User ${user.id}`,
    };
    Sentry.setUser(userIdentity);
    Sentry.setTag('clinician_id', user.id);
    Sentry.setTag(
      'clinic_ids',
      user.clinics?.length
        ? user.clinics.map(c => c.id).join(',')
        : null,
    );

    if (prevUserIdRef.current !== user.id) {
      prevUserIdRef.current = user.id;
      const clinicIds = user.clinics?.length
        ? user.clinics.map(c => String(c.id)).join(',')
        : '';
      identifyUser(String(user.id), true, {
        name: `Clinician ${user.id}`,
        clinic_ids: clinicIds,
      });
    }
  }, [user]);
};

export const useSentryPatientTag = (patientId: number | null) => {
  useEffect(() => {
    Sentry.setTag('patient_id', patientId ?? null);
    return () => {
      Sentry.setTag('patient_id', null);
    };
  }, [patientId]);
};

export const useSentryRouteTracking = (pathname: string) => {
  useEffect(() => {
    Sentry.setTag('route', pathname);
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${pathname}`,
      level: 'info',
    });
  }, [pathname]);
};

export const track = (eventName: string, properties: Record<string, any> = {}) => {
  try {
    trackEvent(eventName, sanitizeSegmentation(properties));
  } catch (_) {
    // Analytics must never disrupt the application.
  }
};

export const ANALYTICS_EVENTS = {
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  HELP_CLICKED: 'Email Support Button Clicked',

  PATIENT_LIST_FILTER_USED: 'Patient List Filter Used',
  PATIENT_RESEND_INVITE_CLICKED: 'Patient Resend Invite Email Clicked',
  PATIENT_DELETED: 'Patient Deleted',
  PATIENT_ADDED: 'New Patient Added',
  PATIENT_EDITED: 'Patient Edited',
  CLINICIAN_ADDED: 'New Clinician Added',
  CLINICIAN_PROFILE_EDITED: 'Clinician Profile Edited',

  PATIENT_LOGGING_TRIAL_SCHEDULED: 'Patient Logging Trial Scheduled',
  TAB_CHANGED: 'Tab Changed',
  PATIENT_NOTE_SAVED: 'Patient Note Saved',
  AGP_DATE_SELECTION: 'AGP Date Selected',
  PATIENT_TIP_SENT: 'Patient Tip Sent',

  FOOD_LOG_FILTER_USED: 'Food Log Filter Used',
  MEAL_CARD_CLICKED: 'Meal Card Clicked',
  MEAL_ADDED: 'Meal Added',
  MEAL_EDITED: 'Meal Edited',
  MEAL_DELETED: 'Meal Deleted',

  GOAL_DELETED: 'Goal Deleted',
  GOAL_EDITED: 'Goal Edited',
  GOAL_ADDED: 'Goal Added',

  NUTRIENT_DATE_CHANGED: 'Nutrition Date Range Changed',
  NUTRIENT_ROW_SELECTED: 'Nutrition Row Selected',
  MEAL_HISTORY_REPORT_DOWNLOADED: 'Meal History Report Downloaded',

  RECIPE_SEARCH_SUBMITTED: 'Recipes Search Submitted',
  RECIPE_LIST_EMAIL_SENT: 'Recipe List Email Sent',

  DRAFT_REPORT_GENERATED: 'Draft Report Generated',
  DRAFT_REPORT_REGENERATED: 'Draft Report Regenerated',
  DRAFT_REPORT_DELETED: 'Draft Report Deleted',
  DRAFT_REPORT_SENT: 'Draft Report Sent',
  REPORT_VIEWED: 'Report Viewed',
  CLINICAL_SUMMARY_CLICKED: 'Clinical Summary Clicked',
  REPORT_LINK_SENT: 'Report Link Sent',
} as const;
